import { createPopper } from '@popperjs/core';
import ApplicationController from '../application_controller';

// Connects to data-controller="tooltip"
class Tooltip extends ApplicationController {
  static targets = ["element", "tooltip"];
  static values = { placement: String };

  connect() {
    // Do not initialize Popper if targets are missing
    if (!this.hasElementTarget || !this.hasTooltipTarget) return;

    this.popperInstance = createPopper(this.elementTarget, this.tooltipTarget, {
      placement: this.placementValue || 'top',
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, 8],
          },
        },
        {
          name: "preventOverflow",
          options: {
            padding: 8,
          },
        },
      ],
    });
  }

  show() {
    this.tooltipTarget.setAttribute("data-show", "");
    this.popperInstance.update();
  }

  hide() {
    this.tooltipTarget.removeAttribute("data-show");
  }

  toggle() {
    if (this.tooltipTarget.hasAttribute("data-show")) {
      this.hide();
    } else {
      this.show();
    }
  }

  // Touch devices synthesize a mouseenter immediately before a click, so
  // binding both show-on-hover and toggle-on-click to the same element
  // causes a tap to show then immediately re-hide the tooltip. These
  // device-aware wrappers make hover and click mutually exclusive by
  // actual input capability instead.
  get supportsHover() {
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  }

  showOnHover() {
    if (this.supportsHover) this.show();
  }

  hideOnHover() {
    if (this.supportsHover) this.hide();
  }

  toggleOnClick() {
    if (!this.supportsHover) this.toggle();
  }

  // Touch devices also fire a focus event on tap once an element is
  // focusable, which would otherwise double-fire alongside toggleOnClick
  // (focus shows it, then the click immediately toggles it back off, so
  // the first tap appears to do nothing). Gating focus/blur behind the same
  // hover check keeps them for real keyboard users on hover-capable
  // desktops, where tap-to-toggle doesn't apply.
  showOnFocus() {
    if (this.supportsHover) this.show();
  }

  hideOnFocus() {
    if (this.supportsHover) this.hide();
  }

  disconnect() {
    if (this.popperInstance) {
      this.popperInstance.destroy();
    }
  }
}

export default Tooltip;
